import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import React from "react";

const settingsLayoutVariants = cva(
  `card-white
  w-full 
  max-w-full
  min-w-0
  box-border
  border 
  border-border 
  rounded-lg 
  shadow-sm`,
  {
    variants: {
      variant: {
        default: "",
        destructive:
          "bg-de-4 text-de-foreground border border-de-2 hover:bg-de/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface SettingsLayoutProps {
  header: React.ReactNode;
  subheader?: string;
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "destructive";
  footer?: React.ReactNode;
  /** Optional control aligned to the right of the header title. */
  headerAction?: React.ReactNode;
  /** Optional control aligned to the right of the subheader line. */
  subheaderAction?: React.ReactNode;
  /** Keeps the header visible while the page scrolls (long editors). */
  stickyHeader?: boolean;
}

export const SettingsLayout = (props: SettingsLayoutProps) => {
  const {
    header,
    subheader,
    children,
    className,
    variant,
    footer,
    headerAction,
    subheaderAction,
    stickyHeader,
  } = props;
  return (
    <div className={cn(settingsLayoutVariants({ variant }), className)}>
      <div
        className={cn(
          "bg-white p-3 md:p-4 border-b",
          variant === "destructive"
            ? "text-de-foreground border-de-3"
            : "text-fg border-border",
          // Stays exactly where it starts. It draws its own top and side borders over the
          // card's (-mx-px/-mt-px) and a strip above it covers the card's borders and shadow
          // in the page margin, so nothing of the card shows there while scrolling.
          stickyHeader &&
            "sticky top-0 z-20 -mx-px -mt-px rounded-t-lg border-x border-t before:absolute before:-inset-x-1 before:bottom-full before:mb-px before:h-2 before:bg-bg before:content-[''] md:before:h-4",
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <h6 className="text-base md:text-lg">{header}</h6>
          {headerAction}
        </div>
        {subheader && (
          <div className="mt-1 flex items-start justify-between gap-4">
            <p className="text-xs text-gr-2">{subheader}</p>
            {subheaderAction}
          </div>
        )}
      </div>

      {children}
      {footer && (
        <div className="flex flex-col sm:flex-row gap-2 justify-end border-t p-3 md:p-4 pt-3 md:pt-4">
          {footer}
        </div>
      )}
    </div>
  );
};

export const ContentColumn1 = (props: {
  description: string;
  width?: string; // Add this prop
  children?: React.ReactNode;
}) => {
  // Full width on mobile; on desktop the description takes the space left by the field column
  const { description, width = "w-full md:flex-1 md:max-w-prose", children } = props;
  return (
    <div className={cn(width, "min-w-0")}>
      <p className="text-sm break-words">{description}</p>
      {props.children && (
        <div className="flex flex-col items-center md:items-start space-y-6 w-full min-w-0">
          {children}
        </div>
      )}
    </div>
  );
};

const columnSizes = {
  sm: "md:w-48 md:shrink-0",
  md: "md:w-80 md:shrink-0",
  lg: "md:w-[28rem] md:shrink-0",
  full: "md:flex-1",
} as const;

export type ContentColumnSize = keyof typeof columnSizes;

// Field column: full width on mobile, fixed width from md so inputs never stretch
export const ContentColumn2 = (props: {
  children: React.ReactNode;
  size?: ContentColumnSize;
}) => {
  const { children, size = "md" } = props;
  return (
    <div className={cn("space-y-6 w-full min-w-0", columnSizes[size])}>
      {children}
    </div>
  );
};

// Shared Cancel / Save buttons for settings forms
export const SettingsFormActions = (props: {
  onCancel: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  cancelDisabled?: boolean;
}) => {
  const { onCancel, isLoading, disabled, cancelDisabled } = props;
  return (
    <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button
        variant="outline"
        type="button"
        size="sm"
        className="w-full sm:w-auto"
        onClick={onCancel}
        disabled={cancelDisabled ?? disabled}
      >
        Cancelar
      </Button>
      <Button
        type="submit"
        size="sm"
        className="w-full sm:w-auto"
        disabled={disabled || isLoading}
        isLoading={isLoading}
      >
        Guardar
      </Button>
    </div>
  );
};

export const SettingsRow = (props: {
  className?: string;
  children: React.ReactNode;
}) => {
  const { children, className } = props;
  return (
    <div
      className={cn(
        "p-3 md:p-4 flex flex-col md:flex-row md:items-start gap-4 md:gap-8 min-w-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

// export const ContentColumn1 = ({
//   className,
//   ...props
// }: React.HTMLAttributes<HTMLDivElement>) => (
//   <div
//     className={cn(
//       "bg-yellow-500 flex flex-col space-y-1.5 text-center sm:text-left",
//       className
//     )}
//     {...props}
//   />
// );
